import { Component } from '@theme/component';

/**
 * Tag-driven product badges.
 *
 * The badge configuration lives in the `badges` section, and Liquid cannot read
 * one section's blocks from another section or a snippet. The section therefore
 * publishes its blocks as JSON and this element matches them against a product's
 * tags at runtime.
 *
 * Sale and Sold Out arrive as entries carrying a `kind` rather than a tag, so
 * they can never collide with a real product tag. Whether a product is on sale
 * is resolved server-side by `snippets/price-state.liquid` and handed over as
 * `data-on-sale`, which keeps the badge and the rendered price in agreement.
 */

const PAYLOAD_ID = 'product-badges';

/** Positions a badge may occupy on a card, mapped to their CSS modifier. */
const CARD_POSITIONS = {
  topLeft: 'top-left',
  topRight: 'top-right',
  bottomLeft: 'bottom-left',
  bottomRight: 'bottom-right',
};

/** Positions a badge may occupy over the product page's main image. */
const PDP_MEDIA_POSITIONS = {
  topLeft: 'top-left',
  topRight: 'top-right',
};

/** Colours the theme editor emits for "no colour", which must not be applied. */
const EMPTY_COLORS = new Set(['', 'rgba(0,0,0,0)']);
const WHITE_COLORS = new Set(['#fff', '#ffffff', 'white', 'rgb(255,255,255)']);

/** @type {object[] | undefined} */
let cachedBadges;

/**
 * Reads and caches the badge configuration published by the `badges` section.
 *
 * @returns {object[]} The configured badges, or an empty list if none are published.
 */
function getBadges() {
  if (cachedBadges) return cachedBadges;

  const payload = document.getElementById(PAYLOAD_ID);
  if (!payload) return (cachedBadges = []);

  try {
    const parsed = JSON.parse(payload.textContent ?? '[]');
    cachedBadges = Array.isArray(parsed) ? parsed : [];
  } catch {
    cachedBadges = [];
  }

  return cachedBadges;
}

/**
 * @param {string | undefined} value - The `data-tags` attribute, a JSON-encoded array.
 * @returns {string[]} The product's tags.
 */
function parseTags(value) {
  if (!value) return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * @param {object} badge - A badge from the published configuration.
 * @returns {HTMLElement} The rendered pill.
 */
function buildBadge(badge) {
  const element = document.createElement('span');
  element.className = 'product-badges__badge product-badges__badge--rectangle';

  const background = badge.background_color ?? '';
  const foreground = badge.text_color ?? '';

  if (!EMPTY_COLORS.has(background)) element.style.backgroundColor = background;

  // A white pill on the white page reads as bare text, so in the page flow it
  // drops its inline padding to line up with the price and title above it.
  if (WHITE_COLORS.has(background.replace(/\s+/g, '').toLowerCase())) {
    element.classList.add('product-badges__badge--plain');
  }
  if (!EMPTY_COLORS.has(foreground)) element.style.color = foreground;

  if (badge.image) {
    const image = document.createElement('img');
    image.src = badge.image;
    image.alt = badge.text ?? '';
    image.loading = 'lazy';
    element.appendChild(image);
  } else {
    // The label is its own element so CSS can truncate it with an ellipsis; the
    // badge itself is a flex container, which text-overflow doesn't apply to.
    const label = document.createElement('span');
    label.className = 'product-badges__label';
    label.textContent = badge.text ?? '';
    element.title = badge.text ?? '';
    element.appendChild(label);
  }

  return element;
}

class ProductBadges extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.renderBadges();
  }

  /** Re-render after the Section Rendering API morphs this element. */
  updatedCallback() {
    super.updatedCallback?.();
    this.renderBadges();
  }

  /** @returns {'card' | 'pdp-media' | 'pdp-page'} Where this instance is mounted. */
  get context() {
    const context = this.dataset.context;
    return context === 'pdp-media' || context === 'pdp-page' ? context : 'card';
  }

  /**
   * Selects the badges that apply to this product.
   *
   * @returns {object[]} The matching badges, in configured order.
   */
  matchBadges() {
    const tags = parseTags(this.dataset.tags);
    const onSale = this.dataset.onSale === 'true';
    const soldOut = this.dataset.available === 'false';
    const hideStatusBadges = this.context !== 'card' && this.dataset.hideStatusBadges === 'true';

    return getBadges().filter((badge) => {
      if (badge.kind === 'sale') return onSale && !hideStatusBadges;
      if (badge.kind === 'sold_out') return soldOut && !hideStatusBadges;

      // An empty required tag matches nothing. Liquid's `contains` would match
      // every product, which is why the one block configured that way is disabled.
      return Boolean(badge.id) && tags.includes(badge.id);
    });
  }

  /**
   * Resolves a badge's CSS position modifier for where this instance is mounted,
   * or null when the badge does not belong on this surface.
   *
   * @param {object} badge - A matched badge.
   * @returns {string | null} The modifier suffix, e.g. `top-left`.
   */
  resolvePosition(badge) {
    if (this.context === 'card') return CARD_POSITIONS[badge.position] ?? null;
    if (this.context === 'pdp-media') return PDP_MEDIA_POSITIONS[badge.position_pdp] ?? null;
    return badge.position_pdp === 'pageRight' ? 'page' : null;
  }

  renderBadges() {
    /** @type {Map<string, HTMLElement>} */
    const wrappers = new Map();

    for (const badge of this.matchBadges()) {
      const position = this.resolvePosition(badge);
      if (!position) continue;

      let wrapper = wrappers.get(position);

      if (!wrapper) {
        wrapper = document.createElement('div');
        wrapper.className = `product-badges product-badges--${position}`;
        wrappers.set(position, wrapper);
      }

      wrapper.appendChild(buildBadge(badge));
    }

    this.replaceChildren(...this.#groupIntoRows(wrappers));
    this.#reserveCardSpace([...wrappers.keys()]);
  }

  /**
   * Stacks sharing an edge go into one row so CSS can split the width between
   * them; absolutely positioned on their own they cannot see each other and
   * overlap. The in-page stack has no corner and is left as it is.
   *
   * @param {Map<string, HTMLElement>} wrappers - The rendered stacks, keyed by position.
   * @returns {HTMLElement[]} The rows, followed by the in-page stack if there is one.
   */
  #groupIntoRows(wrappers) {
    /** @type {HTMLElement[]} */
    const nodes = [];

    for (const edge of ['top', 'bottom']) {
      const stacks = ['left', 'right'].flatMap((side) => wrappers.get(`${edge}-${side}`) ?? []);
      if (stacks.length === 0) continue;

      const row = document.createElement('div');
      row.className = `product-badges-row product-badges-row--${edge}`;
      if (stacks.length > 1) row.classList.add('product-badges-row--paired');

      row.append(...stacks);
      nodes.push(row);
    }

    const page = wrappers.get('page');
    if (page) nodes.push(page);

    return nodes;
  }

  /**
   * Cards reserve room for a badge with a `card-gallery--badge-*` class. Liquid
   * sets it for the sale and sold-out cases, but it cannot know about tag badges,
   * so those are reserved here.
   *
   * @param {string[]} positions - The positions that actually rendered.
   */
  #reserveCardSpace(positions) {
    if (this.context !== 'card') return;

    const gallery = this.closest('.card-gallery');
    if (!gallery) return;

    // Liquid reserves space from the sale and sold-out state alone, and guesses
    // the corner from a theme setting that no longer governs these badges. What
    // actually rendered is only known here, so replace its guess outright.
    for (const name of [...gallery.classList]) {
      if (name.startsWith('card-gallery--badge-')) gallery.classList.remove(name);
    }

    for (const position of positions) {
      gallery.classList.add(`card-gallery--badge-${position}`);
    }
  }
}

if (!customElements.get('product-badges-component')) { 
  customElements.define('product-badges-component', ProductBadges);
}

// The theme editor re-renders the badges section in place when its settings or
// blocks change. Drop the cache and repaint so the preview stays truthful.
document.addEventListener('shopify:section:load', () => {
  cachedBadges = undefined;

  for (const element of document.querySelectorAll('product-badges-component')) {
    if (element instanceof ProductBadges) element.renderBadges();
  }
});
